import { PageHeader, StatCard } from '../components/UI'
import { useAuth } from '../context/AuthContext'
import { useApi } from '../hooks/useApi'
import { formatCurrency, formatDate, timeAgo } from '../utils/helpers'
import { IconUsers, IconBriefcase, IconCurrencyDollar, IconCalendarEvent, IconClipboardList, IconTrendingUp, IconClock, IconCheck } from '@tabler/icons-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'

const PIE_COLORS = ['#206bc4', '#7c3aed', '#f59e0b', '#2fb344', '#d63939']

export default function DashboardPage() {
  const { user, canSeeFinancials } = useAuth()
  const { data: stats, loading: statsLoading } = useApi('/dashboard')
  const { data: activities } = useApi('/activity', { limit: 6 })
  const { data: tasks } = useApi('/tasks', { status: 'pending' })
  const { data: events } = useApi('/events', { upcoming: 'true' })

  if (statsLoading || !stats) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
  }

  const pendingTasks = (tasks || []).slice(0, 5)
  const upcomingEvents = (events || []).slice(0, 3)

  return (
    <>
      <PageHeader
        title={`Welcome back, ${user?.name?.split(' ')[0] || 'there'}`}
        subtitle={`${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} — ${user?.hotel_name || ''}`}
      />

      {/* Stat Cards */}
      <div className="row row-deck row-cards mb-4">
        <div className="col-sm-6 col-lg-3"><StatCard label="Total Leads" value={stats.total_leads} change={`${stats.new_this_week || 0} new this week`} icon={IconUsers} color="primary" /></div>
        <div className="col-sm-6 col-lg-3"><StatCard label="Qualified" value={stats.qualified_leads} icon={IconTrendingUp} color="purple" /></div>
        {canSeeFinancials() && <div className="col-sm-6 col-lg-3"><StatCard label="Pipeline Value" value={formatCurrency(stats.total_pipeline_value)} icon={IconCurrencyDollar} color="green" /></div>}
        {canSeeFinancials() && <div className="col-sm-6 col-lg-3"><StatCard label="Won MTD" value={formatCurrency(stats.closed_won_mtd)} icon={IconBriefcase} color="green" /></div>}
        <div className="col-sm-6 col-lg-3"><StatCard label="Rooms on Books" value={stats.rooms_on_books} icon={IconCalendarEvent} color="primary" /></div>
        <div className="col-sm-6 col-lg-3"><StatCard label="Tasks Due Today" value={stats.tasks_due_today} icon={IconClipboardList} color="yellow" /></div>
        <div className="col-sm-6 col-lg-3"><StatCard label="Pending Proposals" value={stats.proposals_pending} icon={IconClock} color="yellow" /></div>
        <div className="col-sm-6 col-lg-3"><StatCard label="Upcoming Arrivals" value={stats.upcoming_arrivals} icon={IconCalendarEvent} color="primary" /></div>
      </div>

      {/* Charts Row */}
      <div className="row row-deck row-cards mb-4">
        {canSeeFinancials() && (
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header"><h3 className="card-title">Revenue (6 months)</h3></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={stats.revenue_by_month}>
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={v => formatCurrency(v)} />
                    <Bar dataKey="value" fill="#206bc4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
        <div className={canSeeFinancials() ? 'col-lg-4' : 'col-lg-6'}>
          <div className="card">
            <div className="card-header"><h3 className="card-title">Lead Sources</h3></div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={stats.lead_sources} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={75} innerRadius={45} paddingAngle={2}>
                    {(stats.lead_sources || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="d-flex flex-wrap gap-3 mt-2 justify-content-center">
                {(stats.lead_sources || []).map((s, i) => (
                  <span key={s.name} className="d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                    <span className="status-dot" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{s.name} ({s.count})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks + Activity */}
      <div className="row row-deck row-cards mb-4">
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header"><h3 className="card-title">Upcoming Tasks</h3></div>
            <div className="list-group list-group-flush">
              {pendingTasks.map(t => (
                <div key={t.id} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <span className={`status-dot status-dot-animated bg-${t.priority === 'high' ? 'danger' : t.priority === 'medium' ? 'warning' : 'secondary'}`} />
                    </div>
                    <div className="col text-truncate">
                      <div className="text-reset d-block text-truncate">{t.title}</div>
                      <div className="text-secondary text-truncate mt-n1" style={{ fontSize: '0.8rem' }}>{t.related_to} — Due {formatDate(t.due_date, { month: 'short', day: 'numeric' })}</div>
                    </div>
                    <div className="col-auto"><span className={`badge bg-${t.priority === 'high' ? 'red' : 'yellow'}-lt`}>{t.priority}</span></div>
                  </div>
                </div>
              ))}
              {!pendingTasks.length && <div className="list-group-item text-center text-secondary py-4">No pending tasks</div>}
            </div>
          </div>
        </div>
        <div className="col-lg-6">
          <div className="card">
            <div className="card-header"><h3 className="card-title">Recent Activity</h3></div>
            <div className="list-group list-group-flush">
              {(activities || []).slice(0, 6).map(a => (
                <div key={a.id} className="list-group-item">
                  <div className="row align-items-center">
                    <div className="col-auto">
                      <span className={`avatar avatar-sm rounded-circle ${a.type === 'deal' ? 'bg-green-lt' : a.type === 'create' ? 'bg-primary-lt' : a.type === 'email' ? 'bg-purple-lt' : 'bg-secondary-lt'}`}>
                        {a.type === 'deal' ? <IconCheck size={14} /> : (a.user_name || 'U')[0]}
                      </span>
                    </div>
                    <div className="col text-truncate">
                      <div className="d-block text-truncate"><strong>{a.user_name}</strong> {a.action}</div>
                      <div className="text-secondary text-truncate mt-n1" style={{ fontSize: '0.8rem' }}>{a.detail}</div>
                    </div>
                    <div className="col-auto text-secondary" style={{ fontSize: '0.75rem' }}>{timeAgo(a.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <>
          <h3 className="mb-3">Upcoming Events</h3>
          <div className="row row-cards">
            {upcomingEvents.map(ev => (
              <div key={ev.id} className="col-md-4">
                <div className="card" style={{ borderLeft: '3px solid var(--tblr-primary)' }}>
                  <div className="card-body">
                    <h4 className="card-title">{ev.event_name}</h4>
                    <div className="text-secondary mb-2" style={{ fontSize: '0.85rem' }}>{ev.company} — {ev.event_type}</div>
                    <div className="d-flex gap-3" style={{ fontSize: '0.85rem' }}>
                      <span>{formatDate(ev.arrival_date, { month: 'short', day: 'numeric' })} - {formatDate(ev.departure_date, { month: 'short', day: 'numeric' })}</span>
                      <strong>{ev.room_count} rooms</strong>
                    </div>
                    <span className={`badge bg-${ev.status === 'definite' ? 'green' : 'yellow'}-lt mt-2`}>{ev.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}
